import styled, { css, keyframes } from 'styled-components'
import { Button } from '@components/Button'
import { Pill } from '@components/Pill'
import {
	backgroundOrange,
	black,
	blue,
	blueTint,
	border1,
	danger,
	darkGrey,
	darkOrange,
	mutedGrey,
	orange,
	textMuted,
	warmTint,
	white,
} from '@/theme/colors'
import { flatButton } from '@/theme/recipes'
import { layers, motion, radii, shadows } from '@/theme/tokens'
import { fontSizes, text } from '@/theme/typography'

/** A lone photo's width; a grid's too. */
export const PHOTO_GRID_WIDTH = 240

/** One Message's row: mine on the right, others' on the left beside their avatar. */
export const MessageRow = styled.div<{ $mine: boolean; $opensRun: boolean }>`
	position: relative;
	display: flex;
	align-items: flex-end;
	gap: 8px;
	padding: ${({ $opensRun }) => ($opensRun ? '10px' : '3px')} 16px 3px;
	justify-content: ${({ $mine }) => ($mine ? 'flex-end' : 'flex-start')};
`

/** Keeps a run's bubbles in line when the avatar sits only beside the last. */
export const AvatarSlot = styled.span`
	display: inline-flex;
	flex-shrink: 0;
	width: 32px;
`

export const MessageStack = styled.div<{ $mine: boolean; $hasCards: boolean }>`
	position: relative;
	display: flex;
	flex-direction: column;
	align-items: ${({ $mine }) => ($mine ? 'flex-end' : 'flex-start')};
	gap: 4px;
	min-width: 0;
	max-width: min(75%, 560px);
	${({ $hasCards }) =>
		$hasCards &&
		css`
			/* Cards get room to read, however short the text beside them. */
			width: min(75%, 400px);
		`}
`

export const AuthorName = styled.span`
	${text.caption}
	color: ${textMuted};
	padding-left: 2px;
`

const flash = keyframes`
	0% { transform: scale(1); }
	40% { transform: scale(1.04); }
	100% { transform: scale(1); }
`

/** The bubble; a Message of cards or photos alone is just them, without one around them. */
export const Bubble = styled.div<{
	$mine: boolean
	$removed: boolean
	$bubbleless: boolean
	$sending: boolean
	$flash: boolean
	$hasQuote: boolean
	$hasCards: boolean
	$timeRow: boolean
}>`
	position: relative;
	display: flex;
	flex-direction: column;
	gap: 8px;
	max-width: 100%;
	min-width: 72px;
	border-radius: ${radii.card};
	opacity: ${({ $sending }) => ($sending ? 0.6 : 1)};
	transition: opacity ${motion.fast};
	${({ $flash }) =>
		$flash &&
		css`
			animation: ${flash} ${motion.base};
		`}
	${({ $bubbleless, $mine, $removed, $hasQuote, $hasCards, $timeRow }) => {
		const timeRow = $timeRow ? 16 : 0
		if ($bubbleless)
			return css`
				padding: 0 0 ${timeRow}px;
				align-self: stretch;
			`
		return css`
			padding: ${$hasQuote ? 12 : 9}px 14px ${($hasCards ? 12 : 9) + timeRow}px;
			background: ${$removed ? 'transparent' : $mine ? blueTint : backgroundOrange};
			border: 1px solid ${$removed ? darkGrey : $mine ? 'transparent' : border1};
		`
	}}
`

export const BodyText = styled.p<{ $removed: boolean }>`
	${text.body}
	margin: 0;
	color: ${({ $removed }) => ($removed ? mutedGrey : black)};
	white-space: pre-wrap;
	overflow-wrap: anywhere;
	${({ $removed }) =>
		$removed &&
		css`
			font-size: ${fontSizes.base};
			font-style: italic;
		`}
`

/** Blue for what I wrote, orange for others, as their bubbles' accents are: a mention's, a quote's, the reply bar's. */
export const chatAccent = (mine: boolean) => (mine ? blue : orange)

/** A tagged member's `@Name`: orange in others' bubbles, blue on my bubble's blue. */
export const Mention = styled.span<{ $mine: boolean }>`
	font-weight: 500;
	color: ${({ $mine }) => chatAccent($mine)};
`

export const TextLink = styled.a`
	color: inherit;
	text-decoration: underline;
	overflow-wrap: anywhere;
`

/** An invisible copy of the time at the end of the text, which the time is drawn over. */
export const TimeSpacer = styled.span`
	${text.caption}
	font-weight: 400;
	visibility: hidden;
	white-space: nowrap;
	padding-left: 8px;
`

export const Time = styled.span<{ $bubbleless: boolean }>`
	${text.caption}
	font-weight: 400;
	position: absolute;
	right: ${({ $bubbleless }) => ($bubbleless ? 0 : '14px')};
	bottom: ${({ $bubbleless }) => ($bubbleless ? 0 : '7px')};
	color: ${textMuted};
	white-space: nowrap;
	pointer-events: none;
`

export const CardsStack = styled.div`
	display: flex;
	flex-direction: column;
	gap: 6px;
`

/** What a reply quotes: a raw button, a tile that scrolls to the quoted Message (see the raw-button ratchet).
 * A tinted block with an accent bar down its left edge: the block tints for the bubble it's in,
 * the bar and author take the quoted author's accent. On my bubble the block is lightened, not
 * darkened, to keep its grey readable. */
export const QuoteButton = styled.button<{ $inMyBubble: boolean; $quotesMine: boolean }>`
	${flatButton}
	${text.caption}
	font-weight: 400;
	position: relative;
	display: flex;
	flex-direction: column;
	gap: 1px;
	text-align: left;
	padding: 6px 10px 7px 12px;
	border-radius: ${radii.small};
	overflow: hidden;
	background: ${({ $inMyBubble }) => ($inMyBubble ? `${white}66` : `${black}0d`)};
	color: ${textMuted};

	&::before {
		content: '';
		position: absolute;
		top: 0;
		bottom: 0;
		left: 0;
		width: 4px;
		background: ${({ $quotesMine }) => chatAccent($quotesMine)};
	}

	&:disabled {
		cursor: default;
	}
`

export const QuoteAuthor = styled.span<{ $quotesMine: boolean }>`
	font-weight: 500;
	color: ${({ $quotesMine }) => chatAccent($quotesMine)};
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`

export const QuoteText = styled.span`
	display: -webkit-box;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
	overflow: hidden;
	overflow-wrap: anywhere;
`

export const QuoteGlyph = styled.span`
	display: inline-flex;
	vertical-align: -2px;
	margin-right: 4px;
	color: ${textMuted};
`

export const QuoteSnippet = styled.span<{ $removed: boolean }>`
	${({ $removed }) =>
		$removed &&
		css`
			font-style: italic;
			color: ${mutedGrey};
			/* Room for the italic's overhang, which the clamp would otherwise clip. */
			padding-right: 2px;
		`}
`

export const PhotoGrid = styled.div`
	display: flex;
	flex-direction: column;
	gap: 3px;
	width: ${PHOTO_GRID_WIDTH}px;
	max-width: 100%;
	border-radius: ${radii.inner};
	overflow: hidden;
`

export const PhotoRow = styled.div`
	display: flex;
	gap: 3px;
`

/** One photo: a raw button, a tile that opens the photos full screen (see the raw-button ratchet). */
export const PhotoTile = styled.button<{ $height: number }>`
	${flatButton}
	position: relative;
	flex: 1;
	height: ${({ $height }) => $height}px;
	border-radius: ${radii.tiny};
	overflow: hidden;
	background: ${warmTint};

	img {
		width: 100%;
		height: 100%;
		object-fit: cover;
		display: block;
	}
`

export const MoreBadge = styled.span`
	${text.heading}
	position: absolute;
	inset: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	background: ${black}73;
	color: ${white};
`

export const LinkPreviewCard = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
	min-width: 230px;
	padding: 10px;
	border-radius: ${radii.inner};
	border: 1px solid ${border1};
	background: ${white};
`

/** The previewed page: a plain link, opened in a new tab (see the raw-button ratchet). */
export const LinkPreviewLink = styled.a`
	display: flex;
	align-items: flex-start;
	gap: 10px;
	color: ${black};
	text-decoration: none;
`

export const LinkPreviewText = styled.span`
	display: flex;
	flex-direction: column;
	gap: 3px;
	min-width: 0;
`

export const LinkPreviewTitle = styled.span`
	${text.label}
	display: -webkit-box;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
	overflow: hidden;
	overflow-wrap: anywhere;
`

export const LinkPreviewSite = styled.span`
	${text.caption}
	font-weight: 400;
	color: ${textMuted};
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`

/** "+ Add as idea", under the page's title like iOS's, past the image. */
export const LinkPreviewActions = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	padding-left: 74px;
	min-height: 30px;
`

export const AddAsIdeaButton = styled(Button).attrs({ variant: 'tonal', size: 'sm' })``

export const Reactions = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 4px;
`

/** A reaction chip: the Pill as a button; mine stands out. */
export const ReactionChip = styled(Pill).attrs<{ $mine: boolean }>(({ $mine }) => ({
	$tone: $mine ? 'soft' : 'outline',
	$size: 'md',
}))<{ $mine: boolean }>`
	${flatButton}
	gap: 4px;
	border-color: ${({ $mine }) => ($mine ? orange : border1)};
	color: ${({ $mine }) => ($mine ? darkOrange : black)};
	background: ${({ $mine }) => ($mine ? warmTint : white)};
`

/** "Not sent. Click to retry", under a failed send. */
export const FailedButton = styled(Button).attrs({ variant: 'ghost', size: 'sm' })`
	color: ${danger};
	padding: 2px 4px;
`

/** What can be done to a Message, over its bubble's top corner while hovered or picked. */
export const ActionBar = styled.div<{ $mine: boolean; $shown: boolean }>`
	position: absolute;
	top: -18px;
	${({ $mine }) => ($mine ? 'right: 8px;' : 'left: 8px;')}
	z-index: ${layers.raised};
	display: flex;
	gap: 2px;
	padding: 2px;
	border-radius: ${radii.inner};
	background: ${white};
	border: 1px solid ${border1};
	box-shadow: ${shadows.card};
	opacity: ${({ $shown }) => ($shown ? 1 : 0)};
	pointer-events: ${({ $shown }) => ($shown ? 'auto' : 'none')};
	transition: opacity ${motion.fast};

	${MessageRow}:hover &,
	${MessageRow}:focus-within & {
		opacity: 1;
		pointer-events: auto;
	}

	@media (hover: none) {
		${MessageRow}:hover & {
			opacity: ${({ $shown }) => ($shown ? 1 : 0)};
			pointer-events: ${({ $shown }) => ($shown ? 'auto' : 'none')};
		}
	}
`

export const ActionButton = styled(Button).attrs({ variant: 'icon' })`
	width: 32px;
	height: 32px;
`

/** The six reactions, in a palette under the button that opened it. */
export const ReactionPalette = styled.div<{ $top: number; $left: number }>`
	position: fixed;
	top: ${({ $top }) => $top}px;
	left: ${({ $left }) => $left}px;
	z-index: ${layers.popover};
	display: flex;
	gap: 2px;
	padding: 4px;
	border-radius: ${radii.inner};
	background: ${white};
	border: 1px solid ${border1};
	box-shadow: ${shadows.overlay};
`

export const EmojiButton = styled(Button).attrs({ variant: 'icon' })<{ $mine: boolean }>`
	font-size: ${fontSizes.title};
	background: ${({ $mine }) => ($mine ? warmTint : 'transparent')};
`
